import React from 'react';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';
import { useGoogleSearchContext } from './GoogleInstantSearch';
import { buildFilterExpression } from '@jbhi-fi/apis/src/utils/filterExpression';
import { getSortOrderLabel } from './getSortOrderLabel';
import { InfiniteResults } from '../../templates/components/collections/CustomInfiniteHits';
import ResultsSkeleton from '../../templates/components/collections/ResultsSkeleton';
import { NoResultsView } from '../../templates/components/collections/NoResultsView';
import GoogleRefinementContainer from '../../templates/components/refinements/GoogleRefinementContainer';
import type { ContentfulMetafieldCollection } from '@jbhi-fi/apis/src/contentful/types';

type Facet = { facet: string; type: string };

type GoogleCollectionPanelProps = {
  collection: ContentfulMetafieldCollection;
  category?: string;
};

const GoogleCollectionPanel = ({ collection, category }: GoogleCollectionPanelProps) => {
  const {
    hits,
    nbHits,
    facets: facetMap,
    attributionToken,
    isLoading,
    isFetching,
    isLastPage,
    showMore,
    state,
    usedFilter,
  } = useGoogleSearchContext();

  const isDesktop = useBreakpoint('lg', 'up');

  const isHorizontalProductCardEnabled = useFeatureFlag('isHorizontalProductCardEnabled');
  const isProductCardDetailsEnabled = useFeatureFlag('isProductCardDetailsEnabled');
  const isProductCardDetailBadgesEnabled = useFeatureFlag('isProductCardDetailBadgesEnabled');

  const isDesktopView = useBreakpoint('md', 'up');
  const productCardVariant = (() => {
    if (isHorizontalProductCardEnabled) return 'landscape';
    if (!isDesktopView) return 'tablet';
    return 'default';
  })();

  const HIDDEN_FACETS = ['onPromotion', 'bannerTags', 'facets.BannerTags', 'categories'];

  const baseFacets: Array<Facet> =
    collection.facets && Array.isArray(collection.facets) && collection.facets.length
      ? [
          { facet: 'Sold by', type: 'refinementList' },
          { facet: 'isMarketplace', type: 'toggleRefinement' },
          ...collection.facets,
        ]
      : [
          { facet: 'Sold by', type: 'refinementList' },
          { facet: 'Brand', type: 'refinementList' },
          { facet: 'Price', type: 'rangeInput' },
          { facet: 'Category', type: 'refinementList' },
          { facet: 'Artist', type: 'refinementList' },
          { facet: 'isMarketplace', type: 'toggleRefinement' },
        ];

  const orderedFacetKeys: Array<string> = Object.keys(facetMap);
  const apiFacets: Array<Facet> = orderedFacetKeys
    .filter(rawKey => {
      const stripped = rawKey.replace(/^facets\./, '');
      return !HIDDEN_FACETS.includes(rawKey) && !HIDDEN_FACETS.includes(stripped);
    })
    .map(rawKey => {
      const facetName = rawKey.replace(/^facets\./, '');
      const values = facetMap[rawKey] ?? facetMap[`facets.${facetName}`] ?? {};
      if (!Object.keys(values).length) return null;
      return {
        facet: facetName,
        type: facetName === 'Price' ? 'rangeInput' : 'refinementList',
      };
    })
    .filter((f): f is Facet => f !== null);

  const baseFacetNames = new Set(baseFacets.map(f => f.facet.toLowerCase()));
  const extraFacets = apiFacets.filter(f => !baseFacetNames.has(f.facet.toLowerCase()));
  const googleFacets = [...baseFacets, ...extraFacets];

  if (isLoading && hits.length === 0) {
    return (
      <>
        <GoogleRefinementContainer facets={googleFacets} isSearchPage={false} isDesktop={isDesktop} />
        <div className={`collection-results-loop collection-results-loop--${productCardVariant}`}>
          <ResultsSkeleton shouldShimmer variant={productCardVariant} />
        </div>
      </>
    );
  }

  if (!isLoading && hits.length === 0) {
    return (
      <>
        <GoogleRefinementContainer facets={googleFacets} isSearchPage={false} isDesktop={isDesktop} />
        <NoResultsView title="Sorry, we weren't able to find any results.">
          <p>Please try applying fewer filters, or searching for the item you need</p>
        </NoResultsView>
      </>
    );
  }

  return (
    <>
      <GoogleRefinementContainer facets={googleFacets} isSearchPage={false} isDesktop={isDesktop} />
      <InfiniteResults
        hits={hits}
        totalResultsCount={nbHits}
        showLoadMore={true}
        collection={collection}
        hitsPerPage={state.hitsPerPage}
        curatedSection={undefined}
        preezieFinder={null}
        isLoading={isFetching}
        showMore={showMore}
        isLastPage={isLastPage}
        productCardVariant={productCardVariant}
        isProductCardDetailsEnabled={isProductCardDetailsEnabled}
        isProductCardDetailBadgesEnabled={isProductCardDetailBadgesEnabled}
        cloudRetail={{
          attributionToken,
          filter: usedFilter || undefined,
          sortOrder: getSortOrderLabel(state.sortBy, state.order),
          pageCategories: category ? [category] : [],
        }}
      />
    </>
  );
};

export default GoogleCollectionPanel;
